'use client';

import { CaretDown, Check } from '@phosphor-icons/react';
import type { KeyboardEvent } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/utils/cn';

interface FrequencyOption {
  label: string;
  value: string;
}

interface NewsletterFrequencySelectProps {
  id: string;
  inputBaseClassName: string;
  inputNormalClassName: string;
  labelId: string;
  onChange: (value: string) => void;
  value: string;
}

interface DropdownPosition {
  left: number;
  top: number;
  width: number;
}

const FREQUENCY_OPTIONS: readonly FrequencyOption[] = [
  { label: '1 al mes', value: '1' },
  { label: '2 al mes', value: '2' },
];

const VIEWPORT_PADDING = 12;
const DROPDOWN_OFFSET = 4;
const DEFAULT_OPTION = FREQUENCY_OPTIONS[0]!;

const getNextOptionValue = (currentValue: string, direction: 1 | -1) => {
  const currentIndex = FREQUENCY_OPTIONS.findIndex((o) => o.value === currentValue);
  const nextIndex = Math.min(Math.max(currentIndex + direction, 0), FREQUENCY_OPTIONS.length - 1);
  return FREQUENCY_OPTIONS[nextIndex]?.value;
};

const NewsletterFrequencySelect = ({
  id,
  inputBaseClassName,
  inputNormalClassName,
  labelId,
  onChange,
  value,
}: NewsletterFrequencySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = FREQUENCY_OPTIONS.find((o) => o.value === value) ?? DEFAULT_OPTION;

  const closeDropdown = () => setIsOpen(false);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    closeDropdown();
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (event.key === 'Escape') {
      closeDropdown();
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      closeDropdown();
      buttonRef.current?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const nextValue = getNextOptionValue(value, event.key === 'ArrowDown' ? 1 : -1);
    if (nextValue !== undefined) onChange(nextValue);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      closeDropdown();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return undefined;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.min(Math.round(rect.width), window.innerWidth - VIEWPORT_PADDING * 2);
      const left = Math.max(
        VIEWPORT_PADDING,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_PADDING),
      );
      setDropdownPosition({ left, top: rect.bottom + DROPDOWN_OFFSET, width });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={labelId}
        className={cn(
          inputBaseClassName,
          'cursor-pointer pr-9 text-left',
          isOpen ? 'border-cyan ring-adipa-footer-focus-ring ring-2' : inputNormalClassName,
        )}
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        {selectedOption.label}
      </button>

      <CaretDown
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2',
          'text-adipa-footer-field-placeholder duration-adipa-fast transition-transform ease-out',
          isOpen && 'rotate-180',
        )}
        size={14}
        weight="bold"
      />

      {isOpen && dropdownPosition
        ? createPortal(
            <ul
              aria-labelledby={labelId}
              className={cn(
                'adipa-footer-newsletter-dropdown',
                'fixed z-[200] overflow-hidden py-1',
                'rounded-adipa-md border-adipa-footer-line border',
                'bg-adipa-footer-bg animate-[dropdownIn_160ms_ease-out]',
                'shadow-adipa-footer-dropdown',
              )}
              ref={listRef}
              role="listbox"
              style={dropdownPosition}
            >
              {FREQUENCY_OPTIONS.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li
                    aria-selected={isSelected}
                    className={cn(
                      'flex cursor-pointer select-none items-center justify-between px-3 py-2.5',
                      'duration-adipa-fast text-sm transition-colors ease-out',
                      isSelected
                        ? 'bg-adipa-footer-surface-active text-adipa-footer-text-primary font-semibold'
                        : 'text-adipa-footer-text-secondary hover:bg-adipa-footer-surface-hover hover:text-adipa-footer-text-primary font-medium',
                    )}
                    key={option.value}
                    role="option"
                    tabIndex={-1}
                    onClick={() => handleSelect(option.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      handleSelect(option.value);
                    }}
                  >
                    <span>{option.label}</span>
                    {isSelected ? (
                      <Check
                        aria-hidden="true"
                        className="text-cyan ml-2 shrink-0"
                        size={14}
                        weight="bold"
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
};

export default NewsletterFrequencySelect;
